import './TransactionHistoryTable.css';
import { useEffect, useState } from 'react';
import { Alert, Col, Row, Spinner } from 'react-bootstrap';
import YearlyTotalCard from '../YearlyTotalCard/YearlyTotalCard';
import {
    GET_ORDERITEMS_FOR_ORDERS,
    GET_ORDERS_FOR_ACCOUNT_BETWEEN_DATES,
    GET_ORDERS_FOR_CONTACT_BETWEEN_DATES,
    useLazyQuery,
} from 'utils/graphql';
import { useTranslation } from 'react-i18next';

const TransactionHistoryTable = ({
    accountId,
    contactId = null,
    isB2C = false,
    loadTransactionHistory,
}) => {
    const { t } = useTranslation();

    const YEARS_OF_HISTORY = 5;

    const [yearlyOrderHistory, setYearlyOrderHistory] = useState([]);
    const [transactionHistoryError, setTransactionHistoryError] = useState(false);

    const [getOrdersForAccountBetweenDates] = useLazyQuery(GET_ORDERS_FOR_ACCOUNT_BETWEEN_DATES, {
        fetchPolicy: 'cache-and-network',
    });

    const [getOrdersForContactBetweenDates] = useLazyQuery(GET_ORDERS_FOR_CONTACT_BETWEEN_DATES, {
        fetchPolicy: 'cache-and-network',
    });

    const [getOrdersItemsForOrders] = useLazyQuery(GET_ORDERITEMS_FOR_ORDERS, {
        fetchPolicy: 'cache-and-network',
    });

    const calculateYearlyOrderTotals = (year, orders, orderitems) => {
        return {
            year,
            total: orders
                .map((order) => Number(order.totalamount))
                .reduce((prev, next) => prev + next, 0),
            cards: orderitems
                .map((orderitem) => Number(orderitem.quantity))
                .reduce((prev, next) => prev + next, 0),
        };
    };

    const loadOrdersForAccountBetweenDates = async (startDate, endDate) => {
        const { data, error } = await getOrdersForAccountBetweenDates({
            variables: {
                account_id: accountId,
                start_date: startDate,
                end_date: endDate,
            },
        });

        if (error) {
            console.log({ error });
            return null;
        }
        return data;
    };

    const loadOrdersForContactBetweenDates = async (startDate, endDate) => {
        const { data, error } = await getOrdersForContactBetweenDates({
            variables: {
                account_id: accountId,
                contact_id: contactId,
                start_date: startDate,
                end_date: endDate,
            },
        });

        if (error) {
            console.log({ error });
            return null;
        }
        return data;
    };

    const loadOrdersForYear = async (year) => {
        const startDate = `${year}-01-01 00:00:00`;
        const endDate = `${year}-12-31 23:59:59`;

        const ordersData = await (isB2C
            ? loadOrdersForContactBetweenDates(startDate, endDate)
            : loadOrdersForAccountBetweenDates(startDate, endDate));

        if (!ordersData) {
            setTransactionHistoryError(true);
            return;
        }

        let yearlyTotals = {
            year,
            total: 0,
            cards: 0,
        };
        if (ordersData.table.count > 0) {
            const { data: ordersItemsData, error: ordersItemsError } =
                await getOrdersItemsForOrders({
                    variables: {
                        order_ids: ordersData.table.records.map((record) => record.order.sfid),
                    },
                });
            if (ordersItemsError) {
                setTransactionHistoryError(true);
                return;
            }
            yearlyTotals = calculateYearlyOrderTotals(
                year,
                ordersData.table.records.map((record) => record.order),
                ordersItemsData.table.records.map((record) => record.orderitem)
            );
        }
        setYearlyOrderHistory((yearlyOrderHistory) => [...yearlyOrderHistory, yearlyTotals]);
    };

    const loadYearlyTransactionHistory = async () => {
        const currentYear = new Date().getFullYear();
        for (let year = currentYear; year > currentYear - YEARS_OF_HISTORY; year--) {
            await loadOrdersForYear(year);
        }
    };

    useEffect(() => {
        if (!loadTransactionHistory) {
            setYearlyOrderHistory([]);
            return;
        }
        loadYearlyTransactionHistory();
    }, [loadTransactionHistory]);

    return (
        <div className="transaction-history-table">
            <div
                className="history-table"
                style={{ padding: '20px 0', height: `${YEARS_OF_HISTORY * 40 + 40}px` }}
            >
                {yearlyOrderHistory.map((yearlyHistory, i) => (
                    <Row key={`row-${i}`}>
                        <Col>
                            <YearlyTotalCard
                                key={`total-${i}`}
                                year={yearlyHistory.year}
                                total={yearlyHistory.total}
                                isCardTotal={false}
                            />
                        </Col>
                        <Col>
                            <YearlyTotalCard
                                key={`cards-${i}`}
                                year={yearlyHistory.year}
                                total={yearlyHistory.cards}
                            />
                        </Col>
                    </Row>
                ))}
                {YEARS_OF_HISTORY !== yearlyOrderHistory.length && (
                    <Spinner className="spinner-loader" />
                )}
            </div>
            {transactionHistoryError && (
                <Alert
                    variant="danger"
                    className="mb-3 d-flex justify-content-center align-items-center"
                >
                    {t('yearly_transactions_load_fail')}
                </Alert>
            )}
        </div>
    );
};
export default TransactionHistoryTable;
